import { styled } from '@mui/material/styles';
import { Box, Button, Chip, IconButton, MenuItem, Typography } from '@mui/material';
import mixins from '../../../assets/styles/mixins.scss';
import CommonStyles from '../../../assets/styles/CommonStyles';

const ATTEMPTS_COUNT_TYPOGRAPHY = CommonStyles().attemptsCount;
const MCQ_OPTION_SUB_LABEL_TYPOGRAPHY = CommonStyles().mcqOptionSubLabel;

const HEADING_TYPOGRAPHY = {
  color: mixins.zambezi,
  fontFamily: mixins.fontFamilyPlusSans,
  fontSize: mixins.fontSize12,
  fontWeight: mixins.fontWeight400,
  lineHeight: mixins.lineHeight20
};

const CHAPTER_HEADER_TYPOGRAPHY = {
  fontFamily: mixins.fontFamilyPlusSans,
  fontWeight: mixins.fontWeight500,
  fontStyle: 'normal',
  fontSize: mixins.fontSize20,
  lineHeight: mixins.lineHeight175,
  letterSpacing: 0,
  color: mixins.color_text_primary,
  verticalAlign: 'middle',
  marginBottom: 0
};

const QUESTION_TYPOGRAPHY = {
  fontFamily: mixins.fontFamilyPlusSans,
  fontWeight: mixins.fontWeight400,
  fontStyle: 'normal',
  fontSize: mixins.fontSize12,
  lineHeight: mixins.lineHeight20,
  letterSpacing: 0,
  color: mixins.zambezi,
  verticalAlign: 'middle'
};

const QUESTION_TITLE_TYPOGRAPHY = {
  color: mixins.color_text_primary,
  fontFamily: mixins.fontFamilyPlusSans,
  fontSize: mixins.fontSize20,
  fontWeight: mixins.fontWeight500,
  lineHeight: mixins.lineHeight175,
  letterSpacing: '-0.4px'
};

const OPTION_LABEL_TYPOGRAPHY = {
  fontFamily: mixins.fontFamilyPlusSans,
  fontWeight: mixins.fontWeight400,
  fontStyle: 'normal',
  fontSize: mixins.fontSize14,
  lineHeight: mixins.lineHeight22,
  color: mixins.color_text_primary
};

const ASSIGNMENT_NAME_TYPOGRAPHY = {
  color: mixins.zambezi,
  fontFamily: mixins.fontFamilyPlusSans,
  fontSize: mixins.fontSize12,
  fontWeight: mixins.fontWeight400,
  lineHeight: mixins.lineHeight20
};

export const Root = styled(Box)({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    width: '100%',
    boxSizing: 'border-box',
    padding: 16,
    borderRadius: 16,
    backgroundColor: mixins.chalkWhiteFont
  }
});

export const Header = styled(Box)({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: 0
  }
});

export const AssignmentName = styled(Typography)({
  '&&': {
    overflow: 'hidden',
    fontFeatureSettings: '\'liga\' off',
    textOverflow: 'ellipsis',
    fontStyle: 'normal',
    ...ASSIGNMENT_NAME_TYPOGRAPHY
  },
  '&&&.MuiTypography-body1': ASSIGNMENT_NAME_TYPOGRAPHY
});

export const HeaderRow = styled(Box)({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  }
});

export const QuestionRow = styled(Box)({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  }
});

export const Content = styled(Box)({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginTop: 20
  }
});

export const ChapterHeader = styled(Typography)({
  '&&': CHAPTER_HEADER_TYPOGRAPHY,
  '&&&.MuiTypography-body1': {
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight500,
    fontSize: mixins.fontSize20,
    lineHeight: mixins.lineHeight175,
    color: mixins.color_text_primary
  }
});

export const ChapterSubHeader = styled(Typography)({
  '&&': {
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight400,
    fontStyle: 'normal',
    fontSize: mixins.fontSize12,
    lineHeight: mixins.lineHeight16,
    letterSpacing: 0,
    color: mixins.zambezi,
    verticalAlign: 'middle'
  }
});

export const Question = styled(Typography)({
  '&&': QUESTION_TYPOGRAPHY,
  '&&&.MuiTypography-body1': {
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight400,
    fontSize: mixins.fontSize12,
    lineHeight: mixins.lineHeight20,
    color: mixins.zambezi
  }
});

export const AttemptsCount = styled(Typography)({
  '&&': ATTEMPTS_COUNT_TYPOGRAPHY,
  '&&&.MuiTypography-body1': ATTEMPTS_COUNT_TYPOGRAPHY
});

export const FlaggedChip = styled(Chip)({
  '&&': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 8px',
    background: mixins.bulkEditCtaButtonDisabledBGColor,
    color: mixins.color_text_primary,
    borderRadius: 6,
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight500,
    fontSize: mixins.fontSize12,
    lineHeight: mixins.lineHeight16
  }
});

export const Options = styled(Box)({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    width: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden'
  }
});

export const OptionRow = styled(Box)({
  '&&': {
    display: 'grid',
    gridTemplateColumns: '24px 1fr auto',
    alignItems: 'center',
    columnGap: 8,
    width: '100%',
    padding: '10px 12px',
    border: `1px solid ${mixins.tableBorderBottom}`,
    borderRadius: 8,
    background: mixins.chalkWhiteFont,
    boxSizing: 'border-box'
  }
});

export const OptionContent = styled(Box)({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    flex: 1,
    minWidth: 0,
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    overflow: 'hidden'
  }
});

export const OptionLabel = styled(Typography)({
  '&&': {
    ...OPTION_LABEL_TYPOGRAPHY,
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'initial',

    '& p': {
      ...OPTION_LABEL_TYPOGRAPHY,
      whiteSpace: 'normal',
      marginBlockStart: 0,
      marginBlockEnd: 0,
      color: 'inherit'
    }
  },
  '&&&.MuiTypography-body1': OPTION_LABEL_TYPOGRAPHY
});

export const CorrectLabel = styled(OptionLabel)({
  '&&': {
    color: mixins.accentGreen,
    '& p': {
      color: mixins.accentGreen
    }
  },
  '&&&.MuiTypography-body1': {
    ...OPTION_LABEL_TYPOGRAPHY,
    color: mixins.accentGreen
  }
});

export const WrongLabel = styled(OptionLabel)({
  '&&': {
    color: mixins.accentRed,
    '& p': {
      color: mixins.accentRed
    }
  },
  '&&&.MuiTypography-body1': {
    ...OPTION_LABEL_TYPOGRAPHY,
    color: mixins.accentRed
  }
});

export const OptionSubLabel = styled(Typography)({
  '&&': MCQ_OPTION_SUB_LABEL_TYPOGRAPHY,
  '&&&.MuiTypography-body1': MCQ_OPTION_SUB_LABEL_TYPOGRAPHY
});

export const CorrectSubLabel = styled(OptionSubLabel)({
  '&&': {
    color: mixins.accentGreen
  }
});

export const WrongSubLabel = styled(OptionSubLabel)({
  '&&': {
    color: mixins.accentRed
  }
});

export const Correct = styled(OptionRow)({
  '&&': {
    border: `1px solid ${mixins.accentGreenDark}`,
    background: mixins.accentColor
  }
});

export const Wrong = styled(OptionRow)({
  '&&': {
    border: `1px solid ${mixins.accentRedDark}`,
    background: mixins.accentLightRed
  }
});

export const OptionIcon = styled(Box)({
  '&&': {
    justifySelf: 'end'
  }
});

export const StatusIcon = styled(Box)({
  '&&': {
    width: 18,
    height: 18,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
});

export const StatusIconCorrect = styled(StatusIcon)({
  '&&': {
    backgroundColor: mixins.accentGreen
  }
});

export const StatusIconWrong = styled(StatusIcon)({
  '&&': {
    backgroundColor: mixins.accentRed
  }
});

export const OptionPrefix = styled(Box)({
  '&&': {
    width: 24,
    height: 24,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight600,
    fontStyle: 'normal',
    fontSize: mixins.fontSize14,
    lineHeight: mixins.lineHeight24,
    letterSpacing: 0,
    textAlign: 'center',
    border: `1px solid ${mixins.optionPrefixColor}`,
    color: mixins.color_text_primary,
    background: mixins.quaternary,
    padding: 0,
    opacity: 1,
    boxSizing: 'border-box'
  }
});

export const PrefixCorrect = styled(OptionPrefix)({
  '&&': {
    borderColor: mixins.accentGreenDark,
    color: mixins.accentGreen,
    background: mixins.prefixCorrectBGColor
  }
});

export const PrefixWrong = styled(OptionPrefix)({
  '&&': {
    borderColor: mixins.accentRedDark,
    color: mixins.accentRed,
    background: mixins.prefixWrongColor
  }
});

export const Footer = styled(Typography)({
  '&&': {
    color: mixins.zambezi,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize12,
    fontWeight: mixins.fontWeight500,
    lineHeight: mixins.lineHeight20,
    marginTop: 16,
    display: 'flex',
    alignItems: 'center'
  }
});

export const FooterInfoIconButton = styled(IconButton)({
  '&&': {
    padding: 4
  }
});

export const FooterIcon = styled(Box)({
  '&&': {
    width: '16px',
    height: '16px'
  }
});

export const FooterContainer = styled(Box)({
  '&&': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16
  }
});

export const FooterWithOnlyChild = styled(Box)({
  '&&': {
    display: 'contents'
  }
});

export const ReviewAssignmentLink = styled(Typography)({
  '&&': {
    fontFamily: mixins.fontFamilyPlusSans,
    color: mixins.navyColor,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight500,
    lineHeight: mixins.lineHeight22,
    cursor: 'pointer',
    alignItems: 'center',
    '&:hover': {
      textDecoration: 'underline'
    }
  }
});

export const FooterLeftCluster = styled(Box)({
  '&&': {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '8px',
    display: 'flex',
    flexWrap: 'wrap',
    flex: 1,
    minWidth: 0,
    marginTop: 16
  }
});

export const RedirectIcon = styled(Box)({
  '&&': {
    marginBottom: 1
  }
});

export const CourseMenuRoot = styled(Box)({
  '&&': {
    padding: '8px !important'
  }
});

export const MenuItemStyled = styled(MenuItem)({
  '&&': {
    padding: 8,
    minWidth: 150,
    display: 'flex',
    gap: '8px',
    '& p': {
      color: mixins.timeIconColor,
      fontFeatureSettings: '\'liga\' off',
      fontFamily: mixins.fontFamilyPlusSans,
      fontSize: mixins.fontSize14,
      fontStyle: 'normal',
      fontWeight: mixins.fontWeight400,
      lineHeight: mixins.lineHeight22
    },
    '&:hover': {
      backgroundColor: mixins.quaternary
    }
  }
});

export const ThreeDotMenuIcon = styled(Box)({
  '&&': {
    width: 20,
    height: 20
  }
});

export const StatusTagMenuContainer = styled(Box)({
  '&&': {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexShrink: 0
  }
});

export const AskAiCtaIcon = styled(Box)({
  '&&': {
    width: 20,
    height: 20,
    flexShrink: 0
  }
});

export const AskAiCtaHeaderPill = styled(Button)({
  '&&&': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    borderRadius: 999,
    border: '1px solid rgba(107, 75, 196, 0.28)',
    boxShadow: '0 1px 2px rgba(23, 23, 23, 0.06)',
    cursor: 'pointer',
    transition: 'box-shadow 0.2s ease, transform 0.15s ease, border-color 0.2s ease',
    minWidth: '180px',
    justifyContent: 'center',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight600,
    lineHeight: mixins.lineHeight22,
    letterSpacing: 0,
    textTransform: 'none',
    background: `linear-gradient(135deg, ${mixins.amethyst} 0%, #6F4BC4 100%)`,
    color: mixins.chalkWhiteFont
  },
  '&&&:hover': {
    boxShadow: '0 2px 8px rgba(107, 75, 196, 0.18)',
    borderColor: 'rgba(107, 75, 196, 0.45)'
  },
  '&&': {
    '&:focus-visible': {
      outline: '2px solid #6B4BC4',
      outlineOffset: 2
    },
    '& svg': {
      width: 32,
      height: 32
    }
  }
});

export const QuestionStem = styled(Box)({
  '&&': {
    display: 'flex',
    width: '100%'
  }
});

export const QuestionNumberWrapper = styled(Box)({
  '&&': {
    display: 'flex',
    marginRight: 4
  }
});

export const ChapterWrapper = styled(Box)({
  '&&': {
    width: '100%'
  }
});

export const QuestionTitle = styled(Typography)({
  '&&': QUESTION_TITLE_TYPOGRAPHY,
  '&&&.MuiTypography-body1': QUESTION_TITLE_TYPOGRAPHY
});

export const PrimaryCol1 = styled(Box)({
  '&&': {
    padding: '0px 4px'
  }
});

export const HeadingWrapper = styled(Box)({
  '&&': {
    display: 'flex',
    justifyContent: 'space-between'
  }
});

export const Heading = styled(Typography)({
  '&&': HEADING_TYPOGRAPHY,
  '&&&.MuiTypography-body1': HEADING_TYPOGRAPHY
});
